import React, { useState, useEffect, useRef } from 'react';

const AppLoader = ({ loaderStep = 0 }) => {
    const [currentPhase, setCurrentPhase] = useState(loaderStep);
    const [statusTextActive, setStatusTextActive] = useState(true);
    const [particles, setParticles] = useState([]);
    const phaseTimeoutRef = useRef();
    const phases = [
        {
            text: 'Please wait…',
            class: 'phase-4',
            icon: 'loader',
            duration: 3000,
        },
        {
            text: 'Preparing deployment…',
            class: 'phase-1',
            icon: 'loader',
            duration: 5000,
        },
        {
            text: 'Setting up preview…',
            class: 'phase-3',
            icon: 'monitor',
            duration: 500000,
        },
    ];

    // Create particles on mount
    useEffect(() => {
        const particleArray = [];
        for (let i = 0; i < 12; i++) {
            particleArray.push({
                id: i,
                left: Math.random() * 100 + '%',
                animationDelay: Math.random() * 3 + 's',
                animationDuration: 2 + Math.random() * 2 + 's',
            });
        }
        setParticles(particleArray);
    }, []);

    // Handle phase updates
    useEffect(() => {
        const updatePhase = () => {
            // Fade out text
            setStatusTextActive(false);

            setTimeout(() => {
                // Update phase and fade in text
                setCurrentPhase((prev) => (prev + 1) % phases.length);
                setStatusTextActive(true);
            }, 250);
        };

        // Set timeout for next phase
        phaseTimeoutRef.current = setTimeout(updatePhase, phases[currentPhase].duration);

        // Cleanup timeout
        return () => {
            if (phaseTimeoutRef.current) {
                clearTimeout(phaseTimeoutRef.current);
            }
        };
    }, [currentPhase, phases]);

    const renderIcon = (iconType) => {
        const iconStyle = {
            width: '80px',
            height: '80px',
            stroke: '#388EAB',
            fill: 'none',
            strokeWidth: 2.5,
            transition: 'all 0.5s ease',
        };

        switch (iconType) {
            case 'gear':
                return (
                    <svg style={iconStyle} viewBox="0 0 24 24">
                        <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1Z" />
                    </svg>
                );
            case 'clock':
                return (
                    <svg style={iconStyle} viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12,6 12,12 16,14" />
                    </svg>
                );
            case 'database':
                return (
                    <svg style={iconStyle} viewBox="0 0 24 24">
                        <ellipse cx="12" cy="5" rx="9" ry="3" />
                        <path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5" />
                        <path d="M3 12c0 1.66 4.03 3 9 3s9-1.34 9-3" />
                    </svg>
                );
            case 'loader':
                return (
                    <svg style={iconStyle} viewBox="0 0 24 24">
                        <path d="M12 2v4" />
                        <path d="M12 18v4" />
                        <path d="M4.93 4.93l2.83 2.83" />
                        <path d="M16.24 16.24l2.83 2.83" />
                        <path d="M2 12h4" />
                        <path d="M18 12h4" />
                        <path d="M4.93 19.07l2.83-2.83" />
                        <path d="M16.24 7.76l2.83-2.83" />
                    </svg>
                );
            default:
                return null;
        }
    };

    const bodyStyle = {
        margin: 0,
        padding: 0,
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        height: '100%',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    };

    const loaderContainerStyle = {
        textAlign: 'center',
        position: 'relative',
    };

    const iconContainerStyle = {
        width: '120px',
        height: '120px',
        position: 'relative',
        margin: '0 auto 30px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    };

    const statusTextStyle = {
        color: '#388EAB',
        fontSize: '18px',
        fontWeight: 600,
        marginTop: '20px',
        opacity: statusTextActive ? 1 : 0,
        transform: statusTextActive ? 'translateY(0)' : 'translateY(10px)',
        transition: 'all 0.5s ease',
    };

    const progressDotsStyle = {
        display: 'flex',
        justifyContent: 'center',
        gap: '8px',
        marginTop: '15px',
    };

    const particlesStyle = {
        pointerEvents: 'none',
    };

    return (
        <div style={bodyStyle}>
            <style>{`
        /* Phase 1: Generating app - Gear rotation and pulse */
        .phase-1 svg {
          animation: gearRotate 2s linear infinite, gearPulse 1.5s ease-in-out infinite alternate;
        }
        @keyframes gearRotate {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes gearPulse {
          0% { stroke-width: 2.5; filter: drop-shadow(0 0 0 rgba(56, 142, 171, 0.3)); }
          100% { stroke-width: 3.5; filter: drop-shadow(0 0 15px rgba(56, 142, 171, 0.6)); }
        }

        /* Phase 2: Please wait - Clock ticking */
        .phase-2 svg {
          animation: clockTick 1s ease-in-out infinite;
        }
        @keyframes clockTick {
          0%, 100% { transform: scale(1) rotate(0deg); }
          50% { transform: scale(1.1) rotate(5deg); }
        }

        /* Phase 3: Connecting to data sources - Database connection animation */
        .phase-3 svg {
          animation: databaseConnect 2s ease-in-out infinite;
        }
        @keyframes databaseConnect {
          0%, 100% {
            transform: scale(1);
            stroke-width: 2.5;
            filter: drop-shadow(0 0 0 rgba(56, 142, 171, 0.3));
          }
          25% {
            transform: scale(1.05);
            stroke-width: 3;
            filter: drop-shadow(0 0 10px rgba(56, 142, 171, 0.5));
          }
          50% {
            transform: scale(1.1);
            stroke-width: 3.5;
            filter: drop-shadow(0 0 20px rgba(56, 142, 171, 0.7));
          }
          75% {
            transform: scale(1.05);
            stroke-width: 3;
            filter: drop-shadow(0 0 10px rgba(56, 142, 171, 0.5));
          }
        }

        /* Phase 4: Please wait - Loading spinner */
        .phase-4 svg {
          animation: spinnerRotate 1.5s linear infinite;
        }
        @keyframes spinnerRotate {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Particle effects */
        .particle {
          position: absolute;
          width: 3px;
          height: 3px;
          background: #388EAB;
          border-radius: 50%;
          opacity: 0;
          animation: particleFloat 3s ease-in-out infinite;
        }
        @keyframes particleFloat {
          0% { opacity: 0; transform: translateY(20px) scale(0); }
          20% { opacity: 1; transform: translateY(0px) scale(1); }
          80% { opacity: 1; transform: translateY(-30px) scale(1); }
          100% { opacity: 0; transform: translateY(-50px) scale(0); }
        }

        /* Icon container glow effect */
        .icon-container::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 100px;
          height: 100px;
          background: radial-gradient(circle, rgba(56, 142, 171, 0.1) 0%, transparent 70%);
          border-radius: 50%;
          transform: translate(-50%, -50%);
          animation: backgroundGlow 2s ease-in-out infinite alternate;
        }
        @keyframes backgroundGlow {
          0% { opacity: 0.3; transform: translate(-50%, -50%) scale(1); }
          100% { opacity: 0.6; transform: translate(-50%, -50%) scale(1.1); }
        }
      `}</style>

            <div style={loaderContainerStyle}>
                {/* Particles */}
                <div style={particlesStyle}>
                    {particles.map((particle) => (
                        <div
                            key={particle.id}
                            className="particle"
                            style={{
                                left: particle.left,
                                animationDelay: particle.animationDelay,
                                animationDuration: particle.animationDuration,
                            }}
                        />
                    ))}
                </div>

                {/* Icon Container */}
                <div style={iconContainerStyle} className={`icon-container ${phases[currentPhase].class}`}>
                    {renderIcon(phases[currentPhase].icon)}
                </div>

                {/* Status Text */}
                <div style={statusTextStyle}>{phases[currentPhase].text}</div>

                {/* Progress Dots */}
                <div style={progressDotsStyle}>
                    {phases.map((_, index) => (
                        <div
                            key={index}
                            style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                background: index === currentPhase ? '#388EAB' : 'rgba(56, 142, 171, 0.3)',
                                transform: index === currentPhase ? 'scale(1.2)' : 'scale(1)',
                                boxShadow: index === currentPhase ? '0 0 10px rgba(56, 142, 171, 0.5)' : 'none',
                                transition: 'all 0.3s ease',
                            }}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default AppLoader;
